/* Geschenke-Shop – Personalisierung auf der Produktseite.
   Arbeitet mit Horizons Produktformular (buy-buttons) zusammen: Die Felder hängen per
   form-Attribut am Formular, ohne gültige Eingaben lässt der Browser das Formular nicht ab.
   Hier kommt dazu: Variante wählen, Zähler, Live-Vorschau im Buch, Fehler direkt am Feld,
   Zeitstempel der Zustimmung, Kaufleiste am Handy. */

(() => {
  const root = document.documentElement;
  const motionOk = () => root.classList.contains('gs-motion');

  /* ---------- Geldbeträge wie im Shop formatieren ---------- */

  function formatMoney(cents, format) {
    const value = Number(cents) / 100;
    const group = (num, decimals, thousands, decimal) => {
      const [int, frac] = num.toFixed(decimals).split('.');
      const withSep = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? withSep + decimal + frac : withSep;
    };
    return (format || '{{amount_with_comma_separator}} €').replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount':
          return group(value, 2, ',', '.');
        case 'amount_no_decimals':
          return group(value, 0, ',', '.');
        case 'amount_no_decimals_with_comma_separator':
          return group(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return group(value, 2, "'", '.');
        case 'amount_with_comma_separator':
        default:
          return group(value, 2, '.', ',');
      }
    });
  }

  /* ---------- Buch: Name schreibt sich in Goldschrift ---------- */

  class GsBook extends HTMLElement {
    connectedCallback() {
      this.nameEl = this.querySelector('[data-gs-book-name]');
      this.name2El = this.querySelector('[data-gs-book-name2]');
      this.name2Line = this.name2El?.closest('.gs-book__name');
      this.ageEl = this.querySelector('[data-gs-book-age]');
      this.spark = this.querySelector('.gs-book__spark');
      this.writeTimer = 0;
      this.frame = 0;
      this.pending = null;
      this.#setText(this.nameEl, this.nameEl?.textContent || '');

      if (!motionOk() || !('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver(
        ([entry]) => {
          if (!entry?.isIntersecting) return;
          io.disconnect();
          this.write();
        },
        { threshold: 0.5 }
      );
      io.observe(this);
    }

    /** Neue Werte übernehmen – höchstens einmal pro Bild (requestAnimationFrame). */
    update(values) {
      this.pending = { ...(this.pending || {}), ...values };
      if (this.frame) return;
      this.frame = window.requestAnimationFrame(() => {
        this.frame = 0;
        const v = this.pending;
        this.pending = null;
        if (!v) return;
        if ('name' in v) this.#setText(this.nameEl, v.name || this.dataset.sampleName || '');
        if ('name2' in v && this.name2El && this.name2Line) {
          this.#setText(this.name2El, v.name2 ? 'und ' + v.name2 : '');
          this.name2Line.hidden = !v.name2;
        }
        if ('age' in v && this.ageEl) this.ageEl.textContent = v.age || this.dataset.sampleAge || '';
        if ('name' in v || 'name2' in v) this.#scheduleWrite();
      });
    }

    #setText(el, text) {
      if (!el) return;
      el.textContent = text;
      const line = el.closest('.gs-book__name');
      const chars = [...text].length;
      const parts = text.split(/(?<=-)|\s+/).filter(Boolean);
      const wrap = chars > 12 && parts.length > 1;
      const longest = wrap ? Math.max(...parts.map((part) => [...part].length)) : chars;
      line?.classList.toggle('is-wrapped', wrap);
      line?.style.setProperty('--gs-len', String(Math.max(6, longest)));
    }

    #scheduleWrite() {
      this.classList.remove('is-writing');
      window.clearTimeout(this.writeTimer);
      this.writeTimer = window.setTimeout(() => this.write(), 600);
    }

    /** Signaturbewegung: Name deckt sich von links nach rechts auf. */
    write() {
      if (!motionOk() || root.dataset.gsSignature !== 'true') return;
      if (this.spark && this.nameEl) {
        this.spark.style.setProperty('--gs-spark-end', `${this.nameEl.offsetWidth}px`);
        this.spark.style.insetInlineStart = `${this.nameEl.offsetLeft}px`;
      }
      this.classList.remove('is-writing');
      void this.offsetWidth;
      this.classList.add('is-writing');
    }
  }

  if (!customElements.get('gs-book')) customElements.define('gs-book', GsBook);

  /* ---------- Personalisierung ---------- */

  class GsPersonalizer extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.formId || '');
      this.book = document.getElementById(this.dataset.book || '');
      this.fieldsRoot = this.querySelector('[data-gs-fields]');
      this.firstInvalidAt = 0;

      this.#bindVariants();
      this.#bindFields();
      this.#bindValidation();
      this.#bindConsent();
      this.#bindCta();
      this.#bindSticky();
      this.#bindUpload();
      this.#bindVideoStyle();
    }

    /* Videostil (Realistisch / Zeichentrick): Bühnenvideo und Buchvorschau wechseln mit */
    #bindVideoStyle() {
      const radios = this.querySelectorAll('[data-gs-videostil]');
      if (!radios.length) return;
      const apply = (style) => {
        const key = style === 'trick' ? 'Trick' : 'Echt';
        this.dataset.gsStil = style;
        const swapImg = (img) => {
          if (!img || !img.dataset['src' + key]) return;
          img.srcset = img.dataset['srcset' + key] || '';
          img.src = img.dataset['src' + key];
        };
        if (this.book) {
          this.book.dataset.stil = style;
          swapImg(this.book.querySelector('.gs-book__img'));
        }
        const stage = this.querySelector('[data-gs-stage]');
        if (!stage) return;
        swapImg(stage.querySelector('.gs-scene__poster'));
        const video = stage.querySelector('video');
        if (!video) return;
        const small = window.matchMedia('(max-width: 749px)').matches && video.dataset['src' + key + 'Small'];
        const src = small || video.dataset['src' + key];
        if (!src || video.getAttribute('src') === src) return;
        const pausedByUser = stage.querySelector('.gs-media__pause[aria-pressed="true"]');
        video.poster = video.dataset['poster' + key] || '';
        video.src = src;
        video.load();
        if (!pausedByUser && motionOk()) video.play().catch(() => {});
      };
      radios.forEach((r) => r.addEventListener('change', () => { if (r.checked) apply(r.dataset.gsVideostil); }));
      const pre = Array.from(radios).find((r) => r.checked);
      if (pre) apply(pre.dataset.gsVideostil);
    }

    /* Foto-Upload: Vorschau, Dateiname, Haken – Hochladen passiert beim „In den Warenkorb" */
    #bindUpload() {
      this.querySelectorAll('[data-gs-upload]').forEach((zone) => {
        const input = zone.querySelector('input[type="file"]');
        const preview = zone.querySelector('[data-gs-upload-preview]');
        const name = zone.querySelector('[data-gs-upload-name]');
        const error = zone.querySelector('[data-gs-upload-size]');
        const max = Number(zone.dataset.maxMb || 20) * 1024 * 1024;
        if (!input) return;
        input.addEventListener('change', () => {
          const file = input.files?.[0];
          zone.classList.remove('is-done');
          if (error) error.hidden = true;
          if (!file) return;
          if (file.size > max) {
            input.value = '';
            if (error) error.hidden = false;
            return;
          }
          if (name) name.textContent = file.name;
          if (preview) {
            preview.innerHTML = '';
            if (/^image\/(jpeg|png|webp|gif)$/.test(file.type)) {
              const img = document.createElement('img');
              img.alt = '';
              img.src = URL.createObjectURL(file);
              img.onload = () => URL.revokeObjectURL(img.src);
              preview.append(img);
            }
          }
          window.requestAnimationFrame(() => zone.classList.add('is-done'));
        });
      });
    }

    /* Variante: ein Kind oder Geschwister */
    #bindVariants() {
      const radios = this.querySelectorAll('input[name^="gs-variant-"]');
      if (!radios.length) return;
      const apply = (radio) => {
        const idInput = this.form?.querySelector('input[name="id"]');
        if (idInput) idInput.value = radio.value;
        const price = Number(radio.dataset.price || 0);
        if (price > 0) {
          const text = formatMoney(price, this.dataset.moneyFormat);
          this.querySelectorAll('[data-gs-price]').forEach((el) => (el.textContent = text));
        }
        this.#setKind(radio.dataset.kind || '');
      };
      radios.forEach((radio) => {
        radio.addEventListener('change', () => radio.checked && apply(radio));
        if (radio.checked) apply(radio);
      });
    }

    /* Felder je Variantenart ein-/ausblenden (z. B. sibling, digital, print) */
    #setKind(kind) {
      this.dataset.kind = kind;
      this.querySelectorAll('[data-gs-show-kind]').forEach((wrap) => {
        const on = wrap.dataset.gsShowKind.split(' ').includes(kind);
        wrap.hidden = !on;
        wrap.querySelectorAll('input, select, textarea').forEach((input) => {
          if (input.matches('[data-gs-consent-time]')) {
            input.disabled = !on || !input.value;
            return;
          }
          input.disabled = !on;
          const required = on && input.hasAttribute('data-gs-required');
          input.required = required;
          if (required) input.setAttribute('aria-required', 'true');
          else input.removeAttribute('aria-required');
        });
      });
      const name2 = this.querySelector('[data-gs-name2]');
      this.book?.update?.({ name2: kind === 'sibling' ? name2?.value.trim() || '' : '' });
    }

    /* Zähler und Live-Vorschau */
    #bindFields() {
      this.querySelectorAll('[data-gs-count]').forEach((input) => {
        const counter = document.getElementById(input.dataset.gsCount);
        const update = () => {
          if (counter) counter.textContent = `${input.value.length}/${input.maxLength}`;
        };
        input.addEventListener('input', update);
        update();
      });

      const name = this.querySelector('[data-gs-name]');
      const name2 = this.querySelector('[data-gs-name2]');
      const age = this.querySelector('[data-gs-age]');
      name?.addEventListener('input', () => this.book?.update?.({ name: name.value.trim() }));
      name2?.addEventListener('input', () => this.book?.update?.({ name2: name2.value.trim() }));
      age?.addEventListener('change', () => {
        const n = Number(age.value);
        const text = age.value ? age.selectedOptions[0]?.textContent?.trim() || String(n) : '';
        this.book?.update?.({ age: text });
      });
    }

    /* Fehler direkt am Feld statt Browser-Blase */
    #bindValidation() {
      const fields = this.querySelectorAll('[data-gs-field]');
      fields.forEach((field) => {
        const controls = field.querySelectorAll('input, select, textarea');
        const error = field.querySelector('.gs-field__error');
        controls.forEach((control) => {
          control.addEventListener(
            'invalid',
            (event) => {
              event.preventDefault();
              field.classList.add('is-invalid');
              control.setAttribute('aria-invalid', 'true');
              if (error) error.hidden = false;
              this.#focusFirstInvalid(control);
            },
            false
          );
          const clear = () => {
            if (!control.checkValidity()) return;
            field.classList.remove('is-invalid');
            controls.forEach((c) => c.removeAttribute('aria-invalid'));
            if (error) error.hidden = true;
          };
          control.addEventListener('input', clear);
          control.addEventListener('change', clear);
        });
      });
    }

    #focusFirstInvalid(control) {
      const now = performance.now();
      if (now - this.firstInvalidAt < 400) return;
      this.firstInvalidAt = now;
      window.requestAnimationFrame(() => {
        const first = this.fieldsRoot?.querySelector(':is(input, select, textarea):invalid:not([disabled])') || control;
        first.scrollIntoView({ behavior: motionOk() ? 'smooth' : 'auto', block: 'center' });
        first.focus({ preventScroll: true });
      });
    }

    /* Zustimmung: Zeitpunkt als verborgene Eigenschaft */
    #bindConsent() {
      this.querySelectorAll('[data-gs-consent]').forEach((wrap) => {
        const box = wrap.querySelector('input[type="checkbox"]');
        const time = wrap.querySelector('[data-gs-consent-time]');
        if (!box || !time) return;
        box.addEventListener('change', () => {
          time.disabled = !box.checked;
          time.value = box.checked ? new Date().toISOString() : '';
        });
      });
    }

    /* „Jetzt personalisieren" springt zum Formular */
    #bindCta() {
      const target = this.querySelector('.gs-product__form');
      this.querySelectorAll('[data-gs-cta]').forEach((link) => {
        link.addEventListener('click', (event) => {
          if (!target) return;
          event.preventDefault();
          target.scrollIntoView({ behavior: motionOk() ? 'smooth' : 'auto', block: 'start' });
          const first = this.querySelector('input[name^="gs-variant-"]:checked, [data-gs-name]');
          window.setTimeout(() => (first || target).focus({ preventScroll: true }), motionOk() ? 450 : 0);
        });
      });
    }

    /* Kaufleiste am Handy: sichtbar, solange weder Hauptknopf noch Formular im Bild sind */
    #bindSticky() {
      const bar = this.querySelector('[data-gs-sticky]');
      const cta = this.querySelector('.gs-product__cta');
      const form = this.querySelector('.gs-product__form');
      if (!bar || !cta || !form || !('IntersectionObserver' in window)) return;
      bar.hidden = false;
      bar.inert = true;
      const state = { ctaVisible: true, formVisible: false };
      const render = () => {
        const show = !state.ctaVisible && !state.formVisible;
        bar.classList.toggle('is-visible', show);
        bar.inert = !show;
      };
      new IntersectionObserver(([entry]) => {
        if (!entry) return;
        state.ctaVisible = entry.isIntersecting;
        render();
      }).observe(cta);
      new IntersectionObserver(
        ([entry]) => {
          if (!entry) return;
          state.formVisible = entry.isIntersecting;
          render();
        },
        { rootMargin: '0px 0px -30% 0px' }
      ).observe(form);
    }
  }

  /* ---------- Vorher/Nachher-Schieber (range steuert clip-path, per Tastatur bedienbar) ---------- */

  /**
   * Vorher/Nachher-Vergleich: läuft von selbst hin und her, ohne Regler zum Ziehen.
   * Alle Vergleiche auf der Seite hängen an derselben Uhr und bewegen sich deshalb gleichzeitig.
   * Ablauf je Richtung: 1,6 s Bewegung, 2 s Pause. Bei reduzierter Bewegung: fest auf 50 %.
   */
  const COMPARE = { low: 8, high: 92, move: 1600, hold: 2000 };
  const compareSet = new Set();
  let compareRaf = 0;
  const compareEase = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
  const comparePos = (now) => {
    const { low, high, move, hold } = COMPARE;
    const cycle = 2 * (move + hold);
    let t = now % cycle;
    if (t < hold) return low;
    t -= hold;
    if (t < move) return low + (high - low) * compareEase(t / move);
    t -= move;
    if (t < hold) return high;
    t -= hold;
    return high - (high - low) * compareEase(t / move);
  };
  const compareTick = (now) => {
    compareRaf = 0;
    const v = comparePos(now).toFixed(2) + '%';
    let any = false;
    compareSet.forEach((el) => {
      if (!el.visible) return;
      any = true;
      el.style.setProperty('--gs-pos', v);
    });
    if (any) compareRaf = requestAnimationFrame(compareTick);
  };
  const compareStart = () => {
    if (!compareRaf) compareRaf = requestAnimationFrame(compareTick);
  };

  class GsCompare extends HTMLElement {
    connectedCallback() {
      if (!motionOk() || !('IntersectionObserver' in window)) return;
      this.visible = false;
      compareSet.add(this);
      this.io = new IntersectionObserver(([entry]) => {
        this.visible = !!entry?.isIntersecting;
        if (this.visible) compareStart();
      }, { threshold: 0.1 });
      this.io.observe(this);
    }

    disconnectedCallback() {
      compareSet.delete(this);
      this.io?.disconnect();
    }
  }

  if (!customElements.get('gs-compare')) customElements.define('gs-compare', GsCompare);

  if (!customElements.get('gs-personalizer')) customElements.define('gs-personalizer', GsPersonalizer);
})();
